import {
  Sorting,
  ColumnWidthInfo,
  ColumnExtension,
  GridFilter,
} from "../../common/store/types";
import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { AuthorComposer, SortOrder } from "../../../api/graphql";
import type { DataGridColumn } from "../../../common/controls/lists/DataGrid";
import {
  isOperatorAvailable,
  type FilterOperator,
} from "../../../common/services/mrtFilterService";
import { countryDisplayIdByDemonym } from "../../country/store/CountryCommonStore";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../provinceState/store/ProvinceStateCommonStore";
import { authorCollectingSocietyDisplayId } from "../../authorCollectingSociety/store/AuthorCollectingSocietyCommonStore";
import { authorPublisherDisplayId } from "../../authorPublisher/store/AuthorPublisherCommonStore";
import {
  calendarDateToDate,
  diaDeCalendario,
} from "../../../common/calendarDate";

export type ColumnInfo = Record<
  string,
  {
    sortingEnabled: boolean;
    width: number;
    isHidden: boolean;
  }
>;

const VERSION = "1.0.0";

/**
 * La celda de Excel de un date: la medianoche UTC de su día, que ExcelJS escribe como
 * ese día en cualquier zona (cuenta los días en UTC). Sin valor, o sin un día, vacía.
 */
const excelCalendarDate = (value: string | null | undefined): Date | "" => {
  const day = diaDeCalendario(value);
  return day === null ? "" : calendarDateToDate(day);
};

export type RowClickViewMode = "route" | "drawer";

export type AuthorComposerListProfile = {
  VERSION: string;
  columnOrder: string[];
  columnsInfo: ColumnInfo;
  sorting: Sorting[];
  filtering: GridFilter[];
  showColumnFilters?: boolean;
  viewMode?: RowClickViewMode;
};

export class AuthorComposerGridDataStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  errorDescription: string = "";
  errorDialogOpened: boolean = false;
  filtering: GridFilter[] = [];

  // Estado de UI del DataGrid (sincronizado con ListToolbox)
  showColumnFilters: boolean = false;
  isFullScreen: boolean = false;
  columnMenuOpen: boolean = false;
  viewMode: RowClickViewMode = "route";

  get ProfileKey(): string[] {
    return ["AuthorComposer", "list"];
  }

  columnsInfo: ColumnInfo = {};
  initialColumnsInfo: ColumnInfo = {};

  sortExpression: Record<
    string,
    (direction: SortOrder, lang: LangStore) => any
  > = {
    nationality: (direction: SortOrder, _lang: LangStore) => {
      return { nationality: { [_lang.fieldByLang("demonym")]: direction } };
    },
    country: (direction: SortOrder, _lang: LangStore) => {
      return { country: { [_lang.fieldByLang("name")]: direction } };
    },
    province: (direction: SortOrder, _lang: LangStore) => {
      return { province: { [_lang.fieldByLang("name")]: direction } };
    },
    managementSocieties: (direction: SortOrder, _lang: LangStore) => {
      return { managementSocieties: { _count: direction } };
    },
    publishers: (direction: SortOrder, _lang: LangStore) => {
      return { publishers: { _count: direction } };
    },
  };

  initialColumnOrder = [
    "firstName",
    "lastName",
    "aka",
    "nationality",
    "dniDeliveredToSadaicAt",
    "gender",
    "taxIdType",
    "taxId",
    "cuitCertificate",
    "cuitCertificateIssuedAt",
    "cuitCertificateError",
    "cuitCertificateErrorType",
    "dniValidatedAt",
    "email",
    "phone",
    "dateOfBirth",
    "streetAndNumber",
    "floor",
    "suite",
    "postalCode",
    "country",
    "province",
    "city",
    "ipi",
    "managementSocieties",
    "publishers",
    "createdAt_",
    "lastUpdatedAt_",
    "id",
  ];
  columnOrder = [
    "firstName",
    "lastName",
    "aka",
    "nationality",
    "dniDeliveredToSadaicAt",
    "gender",
    "taxIdType",
    "taxId",
    "cuitCertificate",
    "cuitCertificateIssuedAt",
    "cuitCertificateError",
    "cuitCertificateErrorType",
    "dniValidatedAt",
    "email",
    "phone",
    "dateOfBirth",
    "streetAndNumber",
    "floor",
    "suite",
    "postalCode",
    "country",
    "province",
    "city",
    "ipi",
    "managementSocieties",
    "publishers",
    "createdAt_",
    "lastUpdatedAt_",
    "id",
  ];

  _sorting: Sorting[];

  // Restricciones de rol por columna (columnas que requieren roles específicos)
  readonly columnRoleRestrictions: Record<string, string[]> = {
    cuitCertificateError: ["SysAdmin"],
    cuitCertificateErrorType: ["SysAdmin"],
  };

  // Columna de grilla → campo real por el que filtrar. Las pseudo-columnas del
  // snapshot (`data_.X`) no existen en SQL: filtran por la columna promovida
  // con .searchable.
  readonly columnFilterAlias: Record<string, string> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.columnsInfo = {
      firstName: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      lastName: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      aka: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      nationality: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      dniDeliveredToSadaicAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      gender: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      taxIdType: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      taxId: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      cuitCertificate: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      cuitCertificateIssuedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      cuitCertificateError: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      cuitCertificateErrorType: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      dniValidatedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      email: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      phone: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dateOfBirth: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      streetAndNumber: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      floor: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suite: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      postalCode: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      country: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      province: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      city: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      ipi: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      managementSocieties: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publishers: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      createdAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      lastUpdatedAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this.initialColumnsInfo = {
      firstName: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      lastName: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      aka: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      nationality: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      dniDeliveredToSadaicAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      gender: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      taxIdType: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      taxId: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      cuitCertificate: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      cuitCertificateIssuedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      cuitCertificateError: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      cuitCertificateErrorType: {
        sortingEnabled: true,
        width: 300,
        isHidden: !["SysAdmin"].some((role) =>
          this.rootStore.authStore.hasRole(role)
        ),
      },
      dniValidatedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      email: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      phone: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      dateOfBirth: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      streetAndNumber: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      floor: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suite: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      postalCode: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      country: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      province: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      city: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      ipi: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      managementSocieties: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publishers: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      createdAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      lastUpdatedAt_: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this._sorting = [
      { columnName: "lastUpdatedAt_", direction: SortOrder.Desc },
    ];
    makeAutoObservable(this);
  }

  // Verifica si el usuario actual tiene permiso para ver una columna
  isColumnAllowedForUser(columnName: string): boolean {
    const requiredRoles = this.columnRoleRestrictions[columnName];
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // No hay restricción
    }
    return requiredRoles.some((role) => this.rootStore.authStore.hasRole(role));
  }

  get subFilters(): Record<
    string,
    { subFields: string[]; multi: boolean } | undefined
  > {
    return {
      nationality: {
        subFields: [this.langStore.fieldByLang("demonym")],
        multi: false,
      },
      country: {
        subFields: [this.langStore.fieldByLang("name")],
        multi: false,
      },
      province: {
        subFields: [this.langStore.fieldByLang("name")],
        multi: false,
      },
      managementSocieties: {
        subFields: ["memberNumber"],
        multi: true,
      },
      publishers: {
        subFields: ["publisher.pseudonym"],
        multi: true,
      },
    };
  }

  get searchFields(): string[] {
    return ["firstName", "lastName", "aka"];
  }

  get sorting() {
    return this._sorting;
  }

  set sorting(sorting: Sorting[]) {
    this._sorting = sorting;
  }

  get mrtColumnVisibility(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = !info.isHidden;
    }
    return result;
  }

  setMrtColumnVisibility(visibility: Record<string, boolean>) {
    for (const [key, isVisible] of Object.entries(visibility)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].isHidden = !isVisible;
        // Si se oculta la columna, borrar su filtro (UX: lo que ves es lo que filtras)
        if (!isVisible) {
          this.filtering = this.filtering.filter((f) => f.columnName !== key);
        }
      }
    }
  }

  showAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = false;
    }
  }

  hideAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = true;
    }
  }

  // Métodos para estado de UI del DataGrid
  setShowColumnFilters(value: boolean) {
    this.showColumnFilters = value;
  }

  toggleShowColumnFilters() {
    this.showColumnFilters = !this.showColumnFilters;
  }

  setIsFullScreen(value: boolean) {
    this.isFullScreen = value;
  }

  toggleIsFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  setColumnMenuOpen(value: boolean) {
    this.columnMenuOpen = value;
  }

  openColumnMenu() {
    this.columnMenuOpen = true;
  }

  closeColumnMenu() {
    this.columnMenuOpen = false;
  }

  setViewMode(mode: RowClickViewMode) {
    this.viewMode = mode;
  }

  toggleViewMode() {
    this.viewMode = this.viewMode === "drawer" ? "route" : "drawer";
  }

  get mrtColumnSizing(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = info.width;
    }
    return result;
  }

  setMrtColumnSizing(sizing: Record<string, number>) {
    for (const [key, width] of Object.entries(sizing)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].width = width;
      }
    }
  }

  get columnWidths(): ColumnWidthInfo[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return { columnName: key, width: this.columnsInfo[key].width };
    });
  }

  setColumnWidths(columnWidths: ColumnWidthInfo[]) {
    for (const cw of columnWidths) {
      this.columnsInfo[cw.columnName].width = Number(cw.width);
    }
  }

  get columnExtensions(): ColumnExtension[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return {
        columnName: key,
        sortingEnabled: this.columnsInfo[key].sortingEnabled,
      };
    });
  }

  setColumnExtensions(columnExtensions: ColumnExtension[]) {
    for (const ce of columnExtensions) {
      this.columnsInfo[ce.columnName].sortingEnabled = ce.sortingEnabled;
    }
  }

  setColumnOrder(columnOrder: string[]) {
    this.columnOrder = columnOrder;
  }

  resetColumnOrder() {
    this.columnOrder = [...this.initialColumnOrder];
    // Restaurar visibilidad y ancho inicial de columnas
    for (const key of Object.keys(this.columnsInfo)) {
      if (this.initialColumnsInfo[key]) {
        this.columnsInfo[key].isHidden = this.initialColumnsInfo[key].isHidden;
        this.columnsInfo[key].width = this.initialColumnsInfo[key].width;
      }
    }
  }

  setSorting(sorting: Sorting[]) {
    this.sorting = sorting;
  }

  get columnTypes() {
    return {
      firstName: "String",
      lastName: "String",
      aka: "String",
      nationality: "__Reference",
      dniDeliveredToSadaicAt: "DateTime",
      gender: "__Enum",
      taxIdType: "__Enum",
      taxId: "String",
      cuitCertificate: "File",
      cuitCertificateIssuedAt: "DateTime",
      cuitCertificateError: "String",
      cuitCertificateErrorType: "__Enum",
      dniValidatedAt: "DateTime",
      email: "String",
      phone: "String",
      dateOfBirth: "Date",
      streetAndNumber: "String",
      floor: "String",
      suite: "String",
      postalCode: "String",
      country: "__Reference",
      province: "__Reference",
      city: "String",
      ipi: "String",
      managementSocieties: "__SubEntity",
      publishers: "__SubEntity",
      createdAt_: "DateTime",
      lastUpdatedAt_: "DateTime",
      id: "String",
    } as Record<string, string>;
  }

  get filteringColumns() {
    return [
      { columnName: "firstName", filteringEnabled: true },
      { columnName: "lastName", filteringEnabled: true },
      { columnName: "aka", filteringEnabled: true },
      { columnName: "nationality", filteringEnabled: true },
      { columnName: "dniDeliveredToSadaicAt", filteringEnabled: true },
      { columnName: "gender", filteringEnabled: true },
      { columnName: "taxIdType", filteringEnabled: true },
      { columnName: "taxId", filteringEnabled: true },
      { columnName: "cuitCertificate", filteringEnabled: true },
      { columnName: "cuitCertificateIssuedAt", filteringEnabled: true },
      { columnName: "cuitCertificateError", filteringEnabled: true },
      { columnName: "cuitCertificateErrorType", filteringEnabled: true },
      { columnName: "dniValidatedAt", filteringEnabled: true },
      { columnName: "email", filteringEnabled: true },
      { columnName: "phone", filteringEnabled: true },
      { columnName: "dateOfBirth", filteringEnabled: true },
      { columnName: "streetAndNumber", filteringEnabled: true },
      { columnName: "floor", filteringEnabled: true },
      { columnName: "suite", filteringEnabled: true },
      { columnName: "postalCode", filteringEnabled: true },
      { columnName: "country", filteringEnabled: true },
      { columnName: "province", filteringEnabled: true },
      { columnName: "city", filteringEnabled: true },
      { columnName: "ipi", filteringEnabled: true },
      { columnName: "managementSocieties", filteringEnabled: true },
      { columnName: "publishers", filteringEnabled: true },
      { columnName: "createdAt_", filteringEnabled: true },
      { columnName: "lastUpdatedAt_", filteringEnabled: true },
      { columnName: "id", filteringEnabled: true },
    ];
  }

  serverColumn(columnName: string) {
    return columnName;
  }

  setSelectedFilters(filters: GridFilter[]) {
    this.filtering = filters;
  }

  get serverFilters() {
    // Solo aplicar filtros si están visibles (showColumnFilters = true)
    return this.showColumnFilters ? this.filtering : [];
  }

  serverSearch(value: string) {
    return this.searchFields.map((x) => {
      return {
        columnName: x,
        operation: "contains",
        value: value,
      };
    });
  }

  get defaultFilters() {
    return this.filtering;
  }

  get numberColumns() {
    return [];
  }

  get booleanColumns() {
    return [];
  }

  get dateColumns() {
    return [
      "dniDeliveredToSadaicAt",
      "cuitCertificateIssuedAt",
      "dniValidatedAt",
      "dateOfBirth",
      "createdAt_",
      "lastUpdatedAt_",
    ];
  }

  get nullableDateColumns() {
    return [
      "dniDeliveredToSadaicAt",
      "cuitCertificateIssuedAt",
      "dniValidatedAt",
      "dateOfBirth",
      "lastUpdatedAt_",
    ] as string[];
  }

  get xlsColumns(): { header: string; key: string; width: number }[] {
    const columns = [];
    for (const column of this.columnOrder) {
      // Una columna que el proyecto suma al listado (common/components/listExtras.tsx)
      // entra en el orden, porque MRT lo rehace con todas las columnas; no es de la
      // grilla, y no se exporta.
      if (!this.columnsInfo[column]) continue;
      if (
        !this.columnsInfo[column].isHidden &&
        (column.length < 2 || column.slice(0, 2) !== "__")
      ) {
        const width = (this.columnsInfo[column].width / 10) * 1.33;
        columns.push({
          header: this.columnTitles[column],
          key: column,
          width,
        });
      }
    }
    return columns;
  }

  get OrderBy() {
    let orderBy = [];
    for (const s of this.sorting) {
      const sortExpression = this.sortExpression[s.columnName];
      if (sortExpression) {
        const ob = sortExpression(
          s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
          this.langStore
        );
        orderBy.push(ob);
      } else {
        orderBy.push({
          [s.columnName]:
            s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
        });
      }
    }
    return orderBy;
  }

  get columnTitles(): Record<string, string> {
    return {
      firstName: `${this.langStore.AuthorComposerFirstNameLabel}`,
      lastName: `${this.langStore.AuthorComposerLastNameLabel}`,
      gender: `${this.langStore.AuthorComposerGenderLabel}`,
      taxIdType: `${this.langStore.AuthorComposerTaxIdTypeLabel}`,
      taxId: `${this.langStore.AuthorComposerTaxIdLabel}`,
      cuitCertificate: `${this.langStore.AuthorComposerCuitCertificateLabel}`,
      cuitCertificateIssuedAt: `${this.langStore.AuthorComposerCuitCertificateIssuedAtLabel}`,
      cuitCertificateError: `${this.langStore.AuthorComposerCuitCertificateErrorLabel}`,
      cuitCertificateErrorType: `${this.langStore.AuthorComposerCuitCertificateErrorTypeLabel}`,
      dniDeliveredToSadaicAt: `${this.langStore.AuthorComposerDniDeliveredToSadaicAtLabel}`,
      dniValidatedAt: `${this.langStore.AuthorComposerDniValidatedAtLabel}`,
      email: `${this.langStore.AuthorComposerEmailLabel}`,
      phone: `${this.langStore.AuthorComposerPhoneLabel}`,
      nationality: `${this.langStore.AuthorComposerNationalityLabel}`,
      dateOfBirth: `${this.langStore.AuthorComposerDateOfBirthLabel}`,
      streetAndNumber: `${this.langStore.AuthorComposerStreetAndNumberLabel}`,
      floor: `${this.langStore.AuthorComposerFloorLabel}`,
      suite: `${this.langStore.AuthorComposerSuiteLabel}`,
      postalCode: `${this.langStore.AuthorComposerPostalCodeLabel}`,
      country: `${this.langStore.AuthorComposerCountryLabel}`,
      province: `${this.langStore.AuthorComposerProvinceLabel}`,
      city: `${this.langStore.AuthorComposerCityLabel}`,
      aka: `${this.langStore.AuthorComposerAkaLabel}`,
      ipi: `${this.langStore.AuthorComposerIpiLabel}`,
      managementSocieties: `${this.langStore.AuthorComposerManagementSocietiesLabel}`,
      publishers: `${this.langStore.AuthorComposerPublishersLabel}`,
      createdAt_: `${this.langStore.AuthorComposerCreatedAt_Label}`,
      lastUpdatedAt_: `${this.langStore.AuthorComposerLastUpdatedAt_Label}`,
      id: `Id`,
    };
  }

  // Un date (día de calendario) filtra en su propia columna, sin zona ni hora
  // ('calendarDate'); un dateTime sigue siendo 'date'.
  private getMrtColumnType(
    type: string
  ): "string" | "number" | "date" | "calendarDate" | "boolean" {
    if (type === "Int" || type === "Decimal") return "number";
    if (type === "Date") return "calendarDate";
    if (type === "DateTime") return "date";
    if (type === "Boolean") return "boolean";
    return "string";
  }

  get dataGridColumns(): DataGridColumn<Record<string, any>>[] {
    // Usar initialColumnOrder (orden fijo) para definir columnas
    // MRT maneja reorden visual con prop columnOrder separada
    // Esto permite que "Reset Order" funcione correctamente
    const nullableDateColumns = this.nullableDateColumns;
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => {
        const columnType = this.columnTypes[name];
        const filterCol = this.filteringColumns.find(
          (f: any) => f.columnName === name
        );
        const baseColumn = {
          header: this.columnTitles[name] || name,
          size: this.columnsInfo[name]?.width || 150,
          columnType: this.getMrtColumnType(columnType),
          nullable: nullableDateColumns.includes(name),
          filterEnabled: filterCol?.filteringEnabled ?? true,
          sortingEnabled: this.columnsInfo[name]?.sortingEnabled ?? true,
        };

        // JSON fields need accessorFn to stringify the value
        if (columnType === "Json") {
          return {
            ...baseColumn,
            id: name,
            accessorFn: (row: any) => {
              const val = row[name];
              if (val == null) return "";
              const s = JSON.stringify(val);
              return s.length > 100 ? s.substring(0, 100) + "..." : s;
            },
          };
        }

        return {
          ...baseColumn,
          accessorKey: name,
        };
      });
  }

  // Lista de columnas para el ColumnMenu (usado en ListToolbox)
  get columnMenuItems(): { id: string; header: string }[] {
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => ({
        id: name,
        header: this.columnTitles[name] || name,
      }));
  }

  authorComposerFormat(e: AuthorComposer) {
    return {
      firstName: e.firstName,
      lastName: e.lastName,
      aka: e.aka,
      nationality: e.nationality
        ? countryDisplayIdByDemonym(e.nationality, this.langStore)
        : "",
      dniDeliveredToSadaicAt: e.dniDeliveredToSadaicAt
        ? new Date(e.dniDeliveredToSadaicAt)
        : "",
      gender: e.gender,
      taxIdType: e.taxIdType,
      taxId: e.taxId,
      cuitCertificate: e.cuitCertificate,
      cuitCertificateIssuedAt: e.cuitCertificateIssuedAt
        ? new Date(e.cuitCertificateIssuedAt)
        : "",
      cuitCertificateError: e.cuitCertificateError,
      cuitCertificateErrorType: e.cuitCertificateErrorType,
      dniValidatedAt: e.dniValidatedAt ? new Date(e.dniValidatedAt) : "",
      email: e.email,
      phone: e.phone,
      dateOfBirth: excelCalendarDate(e.dateOfBirth),
      streetAndNumber: e.streetAndNumber,
      floor: e.floor,
      suite: e.suite,
      postalCode: e.postalCode,
      country: e.country ? countryDisplayId(e.country, this.langStore) : "",
      province: e.province
        ? provinceStateDisplayId(e.province, this.langStore)
        : "",
      city: e.city,
      ipi: e.ipi,
      managementSocieties: e.managementSocieties
        ? e.managementSocieties.map((x: any) =>
            authorCollectingSocietyDisplayId(x, this.langStore)
          )
        : [],
      publishers: e.publishers
        ? e.publishers.map((x: any) =>
            authorPublisherDisplayId(x, this.langStore)
          )
        : [],
      createdAt_: new Date(e.createdAt_),
      lastUpdatedAt_: e.lastUpdatedAt_ ? new Date(e.lastUpdatedAt_) : "",
      id: e.id,
    };
  }

  get gridState(): any {
    // Solo guardar filtros si están habilitados Y tienen valores
    const hasActiveFilters =
      this.showColumnFilters && this.filtering.length > 0;
    return {
      columnOrder: this.columnOrder,
      columnsInfo: this.columnsInfo,
      sorting: this.sorting,
      filtering: hasActiveFilters ? this.filtering : [],
      // showColumnFilters solo true si hay filtros activos
      showColumnFilters: hasActiveFilters,
      viewMode: this.viewMode,
      VERSION,
    };
  }

  setGridState(gridState: AuthorComposerListProfile) {
    // Nunca debe fallar la carga del profile - el cliente manda, el profile sugiere
    try {
      if (!gridState || typeof gridState !== "object") return;
      // No rechazar por VERSION diferente - validamos cada campo individualmente
      // Esto permite migrar profiles de versiones anteriores

      const validColumns = new Set(Object.keys(this.columnsInfo));

      if (gridState.columnsInfo && typeof gridState.columnsInfo === "object") {
        const mergedColumnsInfo: ColumnInfo = {};

        for (const [key, defaultValue] of Object.entries(this.columnsInfo)) {
          mergedColumnsInfo[key] = { ...defaultValue };
        }

        for (const [key, savedValue] of Object.entries(gridState.columnsInfo)) {
          if (
            validColumns.has(key) &&
            typeof savedValue === "object" &&
            savedValue !== null
          ) {
            const defaultValue = mergedColumnsInfo[key];

            mergedColumnsInfo[key] = {
              sortingEnabled:
                typeof savedValue.sortingEnabled === "boolean"
                  ? savedValue.sortingEnabled
                  : defaultValue.sortingEnabled,

              width:
                typeof savedValue.width === "number" &&
                savedValue.width > 0 &&
                isFinite(savedValue.width)
                  ? savedValue.width
                  : defaultValue.width,

              isHidden:
                typeof savedValue.isHidden === "boolean"
                  ? savedValue.isHidden
                  : defaultValue.isHidden,
            };
          }
        }

        this.columnsInfo = mergedColumnsInfo;
      }

      if (Array.isArray(gridState.columnOrder)) {
        const validOrderColumns = gridState.columnOrder.filter(
          (col) => typeof col === "string" && validColumns.has(col)
        );
        const newColumns = Array.from(validColumns).filter(
          (col) => !gridState.columnOrder.includes(col)
        );
        this.columnOrder = [...validOrderColumns, ...newColumns];
      }

      if (Array.isArray(gridState.sorting)) {
        this.sorting = gridState.sorting.filter(
          (sort) =>
            typeof sort === "object" &&
            sort !== null &&
            typeof sort.columnName === "string" &&
            validColumns.has(sort.columnName) &&
            (sort.direction === "asc" || sort.direction === "desc")
        );
        if (this.sorting.length === 0) {
          this.sorting = [
            { columnName: "lastUpdatedAt_", direction: SortOrder.Desc },
          ];
        }
      }

      if (Array.isArray(gridState.filtering)) {
        const nullableDateColumns = this.nullableDateColumns;
        this.filtering = gridState.filtering.filter(
          (filter) =>
            typeof filter === "object" &&
            filter !== null &&
            typeof filter.columnName === "string" &&
            validColumns.has(filter.columnName) &&
            typeof filter.operation === "string" &&
            filter.hasOwnProperty("value") &&
            // "Vacío" y "No vacío" solo en una fecha que puede ser nula. Un perfil
            // guardado cuando la columna era otra cosa no puede dejar un filtro que
            // el server rechaza en cada carga (el DataGrid también lo descarta, pero
            // el perfil puede llegar acá antes).
            isOperatorAvailable(
              this.getMrtColumnType(this.columnTypes[filter.columnName]),
              filter.operation as FilterOperator,
              nullableDateColumns.includes(filter.columnName)
            )
        );
      }

      // Si hay filtros guardados, mostrar los filtros automáticamente
      // Si el profile guardó showColumnFilters, usar ese valor
      if (typeof gridState.showColumnFilters === "boolean") {
        this.showColumnFilters = gridState.showColumnFilters;
      } else if (this.filtering.length > 0) {
        // Compatibilidad: si hay filtros pero no se guardó showColumnFilters, activarlos
        this.showColumnFilters = true;
      }

      if (gridState.viewMode === "route" || gridState.viewMode === "drawer") {
        this.viewMode = gridState.viewMode;
      }
    } catch (error) {
      // Silenciar errores de carga de profile - usar valores por defecto
      console.warn(
        "[GridDataStore] Error loading profile, using defaults:",
        error
      );
    }
  }
}
